import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Link as LinkIcon } from "lucide-react";
import { compressImage } from "@/lib/imageCompress";

const MAX_PHOTO_BYTES = 15 * 1024 * 1024; // 15MB raw, will be compressed

export default function PersonDialog({ open, onOpenChange, editing, onDone }) {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [alias, setAlias] = useState("");
    const [organization, setOrganization] = useState("");
    const [rank, setRank] = useState("");
    const [phone, setPhone] = useState("");
    const [notes, setNotes] = useState("");
    const [photoUrl, setPhotoUrl] = useState("");
    const [deceased, setDeceased] = useState(false);
    const [orgList, setOrgList] = useState([]);
    const [photoUrlInput, setPhotoUrlInput] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        api.get("/organizations")
            .then((r) => setOrgList(r.data || []))
            .catch(() => setOrgList([]));
        if (editing) {
            setFirstName(editing.first_name || "");
            setLastName(editing.last_name || "");
            setAlias(editing.alias || "");
            setOrganization(editing.organization || "");
            setRank(editing.rank || "");
            setPhone(editing.phone || "");
            setNotes(editing.notes || "");
            setPhotoUrl(editing.photo_url || "");
            setDeceased(!!editing.deceased);
            setPhotoUrlInput("");
        } else {
            setFirstName("");
            setLastName("");
            setAlias("");
            setOrganization("");
            setRank("");
            setPhone("");
            setNotes("");
            setPhotoUrl("");
            setDeceased(false);
            setPhotoUrlInput("");
        }
    }, [open, editing]);

    const onPhotoChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > MAX_PHOTO_BYTES) {
            toast.error("Photo trop lourde (max 15 MB)");
            return;
        }
        try {
            const dataUrl = await compressImage(file, { maxDim: 800, quality: 0.82 });
            setPhotoUrl(dataUrl);
        } catch {
            toast.error("Impossible de lire / compresser la photo");
        }
    };

    const applyPhotoUrl = () => {
        const url = photoUrlInput.trim();
        if (!url) return;
        if (!/^https?:\/\/.+/i.test(url) && !url.startsWith("data:image/")) {
            toast.error("Lien invalide (doit commencer par http(s)://)");
            return;
        }
        setPhotoUrl(url);
        setPhotoUrlInput("");
    };

    const submit = async () => {
        if (!firstName.trim() || !lastName.trim())
            return toast.error("Prénom et nom requis");
        setLoading(true);
        try {
            const payload = {
                first_name: firstName,
                last_name: lastName,
                alias,
                organization,
                rank,
                phone,
                notes,
                photo_url: photoUrl,
                deceased,
            };
            if (editing) {
                await api.patch(`/persons/${editing.id}`, payload);
                toast.success("Fiche mise à jour");
            } else {
                await api.post("/persons", payload);
                toast.success("Fiche créée");
            }
            onOpenChange(false);
            onDone && onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        }
        setLoading(false);
    };

    const initials = `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase() || "?";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="person-dialog" className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">
                        {editing ? "Modifier la fiche" : "Nouvelle fiche"}
                    </DialogTitle>
                    <DialogDescription>
                        Informations sur un contact et son organisation.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    {/* Photo */}
                    <div className="flex items-center gap-4">
                        <div className="w-20 h-20 rounded-full overflow-hidden bg-[#DDE5D0] text-[#3D4E2E] flex items-center justify-center shrink-0 border border-[#3D4E2E]/20">
                            {photoUrl ? (
                                <img
                                    src={photoUrl}
                                    alt="Aperçu"
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <span className="font-display text-xl font-semibold">
                                    {initials}
                                </span>
                            )}
                        </div>
                        <div className="flex-1">
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Photo
                            </Label>
                            <div className="mt-2 flex items-center gap-2">
                                <label
                                    htmlFor="person-photo-input"
                                    className="inline-flex items-center gap-2 h-10 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer text-sm text-slate-700"
                                >
                                    <Upload className="w-4 h-4" />
                                    Choisir…
                                </label>
                                <input
                                    id="person-photo-input"
                                    data-testid="person-photo-input"
                                    type="file"
                                    accept="image/*"
                                    onChange={onPhotoChange}
                                    className="hidden"
                                />
                                {photoUrl && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        data-testid="person-photo-clear-btn"
                                        onClick={() => setPhotoUrl("")}
                                        className="text-slate-500 hover:text-rose-700 hover:bg-rose-50"
                                    >
                                        <X className="w-4 h-4" />
                                    </Button>
                                )}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                                JPG/PNG · compressée automatiquement
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                                <div className="relative flex-1">
                                    <LinkIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <Input
                                        data-testid="person-photo-url-input"
                                        value={photoUrlInput}
                                        onChange={(e) => setPhotoUrlInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                applyPhotoUrl();
                                            }
                                        }}
                                        placeholder="Ou collez un lien (https://…)"
                                        className="h-9 pl-9 text-sm"
                                    />
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={applyPhotoUrl}
                                    data-testid="person-photo-url-add-btn"
                                    className="h-9"
                                >
                                    Ajouter
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Prénom
                            </Label>
                            <Input
                                data-testid="person-firstname-input"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                className="mt-2 h-11"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Nom
                            </Label>
                            <Input
                                data-testid="person-lastname-input"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                className="mt-2 h-11"
                            />
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                            Surnom
                        </Label>
                        <Input
                            data-testid="person-alias-input"
                            value={alias}
                            onChange={(e) => setAlias(e.target.value)}
                            placeholder="Ex : El Diablo"
                            className="mt-2 h-11"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Organisation
                            </Label>
                            <Select
                                value={organization || "__none__"}
                                onValueChange={(v) =>
                                    setOrganization(v === "__none__" ? "" : v)
                                }
                            >
                                <SelectTrigger
                                    data-testid="person-organization-select"
                                    className="mt-2 h-11"
                                >
                                    <SelectValue placeholder="Aucune" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">Aucune</SelectItem>
                                    {orgList.map((o) => (
                                        <SelectItem key={o.id} value={o.name}>
                                            {o.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Rang / Rôle
                            </Label>
                            <Input
                                data-testid="person-rank-input"
                                value={rank}
                                onChange={(e) => setRank(e.target.value)}
                                placeholder="President, VP, Sergeant…"
                                className="mt-2 h-11"
                            />
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                            Téléphone
                        </Label>
                        <Input
                            data-testid="person-phone-input"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="555-0123"
                            className="mt-2 h-11"
                        />
                    </div>

                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                            Notes
                        </Label>
                        <Textarea
                            data-testid="person-notes-input"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            className="mt-2"
                            placeholder="Compétences, alliances, comportements, historique…"
                        />
                    </div>

                    <label
                        htmlFor="person-deceased-input"
                        className="flex items-start gap-3 p-3 rounded-md border border-slate-200 bg-slate-50 cursor-pointer hover:bg-slate-100"
                    >
                        <input
                            id="person-deceased-input"
                            data-testid="person-deceased-input"
                            type="checkbox"
                            checked={deceased}
                            onChange={(e) => setDeceased(e.target.checked)}
                            className="mt-1 h-4 w-4 accent-slate-900"
                        />
                        <div>
                            <div className="text-sm font-medium text-slate-900">
                                Marquer comme décédé(e)
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                                La fiche sera affichée avec un traitement spécial (†,
                                grisée).
                            </div>
                        </div>
                    </label>
                </div>

                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        data-testid="person-cancel-btn"
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={loading}
                        data-testid="person-submit-btn"
                        className="bg-[#3D4E2E] hover:bg-[#2E3A22] text-white"
                    >
                        {loading ? "Enregistrement…" : editing ? "Enregistrer" : "Créer"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
